import type { Icon } from '@phosphor-icons/react'
import {
  BookOpenIcon,
  BuildingsIcon,
  CalendarCheckIcon,
  CalendarIcon,
  ChartBarIcon,
  ChartLineUpIcon,
  ChartPieSliceIcon,
  ChatsCircleIcon,
  ClipboardTextIcon,
  CompassIcon,
  ConfettiIcon,
  CopySimpleIcon,
  CurrencyDollarIcon,
  EnvelopeSimpleIcon,
  FilesIcon,
  FileTextIcon,
  FolderOpenIcon,
  GearSixIcon,
  GlobeIcon,
  HandshakeIcon,
  LightningIcon,
  ListChecksIcon,
  MagicWandIcon,
  MegaphoneIcon,
  NoteIcon,
  PlugsConnectedIcon,
  PulseIcon,
  ReceiptIcon,
  RobotIcon,
  RocketIcon,
  RocketLaunchIcon,
  SparkleIcon,
  StackIcon,
  StorefrontIcon,
  TargetIcon,
  TicketIcon,
  TimerIcon,
  TreeStructureIcon,
  TruckIcon,
  UserIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react'

/**
 * Presentation metadata for help categories (help_entry.module_slug). Kept in
 * step with apps/web/src/components/help/help-display.ts — when a category is
 * added there, add it here too or it renders with the folder fallback.
 */

export type CategoryHue =
  | 'amber'
  | 'blue'
  | 'emerald'
  | 'violet'
  | 'rose'
  | 'sky'
  | 'orange'
  | 'teal'
  | 'indigo'
  | 'slate'

interface CategoryMeta {
  label?: string
  description: string
  icon: Icon
  hue: CategoryHue
}

const LABEL_OVERRIDES: Record<string, string> = {
  crm: 'CRM',
  financials: 'Profitability',
  agents: 'Agent Accounts',
  enrichment: 'Data Quality',
}

export const START_HERE_SLUGS = ['getting-started', 'benny']

const CATEGORIES: Record<string, CategoryMeta> = {
  'getting-started': {
    description: 'Workspace setup, onboarding, and first-week guidance.',
    icon: RocketLaunchIcon,
    hue: 'violet',
  },
  admin: {
    description: 'Account, workspace, and team settings.',
    icon: BuildingsIcon,
    hue: 'slate',
  },
  analytics: {
    description: 'Reports, dashboards, and metric alerts.',
    icon: ChartBarIcon,
    hue: 'blue',
  },
  benny: {
    description:
      'Meet Benny — your Scrambl teammate for answers and quick actions.',
    icon: SparkleIcon,
    hue: 'violet',
  },
  calendar: {
    description: 'Scheduling, time blocking, and connected calendars.',
    icon: CalendarIcon,
    hue: 'orange',
  },
  clients: {
    description: 'Client and entity records, health, and portals.',
    icon: BuildingsIcon,
    hue: 'blue',
  },
  collections: {
    description: 'Custom collections, fields, and linked records.',
    icon: StackIcon,
    hue: 'teal',
  },
  communications: {
    description: 'Calls, email, meetings, messages, and sales sequences.',
    icon: EnvelopeSimpleIcon,
    hue: 'sky',
  },
  contacts: {
    description: 'Contact records, relationships, and CRM details.',
    icon: UserIcon,
    hue: 'sky',
  },
  contracts: {
    description: 'Contracts, retainers, and agreement workflows.',
    icon: FileTextIcon,
    hue: 'indigo',
  },
  crm: {
    description: 'Contacts, deals, and sales pipelines.',
    icon: ChartLineUpIcon,
    hue: 'rose',
  },
  deals: {
    description: 'Pipeline stages, opportunities, and deal management.',
    icon: HandshakeIcon,
    hue: 'emerald',
  },
  deployments: {
    description: 'Releases, environments, and CI-connected deployments.',
    icon: RocketIcon,
    hue: 'indigo',
  },
  discussions: {
    description: 'Threaded conversations across your workspace.',
    icon: ChatsCircleIcon,
    hue: 'sky',
  },
  documents: {
    description: 'Files, links, and document organization.',
    icon: FilesIcon,
    hue: 'indigo',
  },
  events: {
    description: 'Event planning, schedules, and attendees.',
    icon: ConfettiIcon,
    hue: 'rose',
  },
  expenses: {
    description: 'Expense capture, approvals, and reimbursement.',
    icon: CurrencyDollarIcon,
    hue: 'teal',
  },
  focus: {
    description: 'Daily focus plans for distraction-free work.',
    icon: TargetIcon,
    hue: 'violet',
  },
  forms: {
    description: 'Form building, sharing, and submissions.',
    icon: ClipboardTextIcon,
    hue: 'violet',
  },
  integrations: {
    description: 'Connect Slack, email, calendars, GitHub, and more.',
    icon: PlugsConnectedIcon,
    hue: 'emerald',
  },
  financials: {
    label: 'Profitability',
    description: 'P&L, forecast, billing, earnings approvals, and rates.',
    icon: ChartPieSliceIcon,
    hue: 'emerald',
  },
  agents: {
    label: 'Agent Accounts',
    description:
      'Connect AI tools as accountable teammates with their own access.',
    icon: RobotIcon,
    hue: 'violet',
  },
  'task-templates': {
    description: 'Reusable subtask trees and checklists for repeatable work.',
    icon: CopySimpleIcon,
    hue: 'emerald',
  },
  enrichment: {
    label: 'Data Quality',
    description:
      'Merge duplicate contacts and enrich records from your provider.',
    icon: MagicWandIcon,
    hue: 'sky',
  },
  'web-tracking': {
    description: 'Website visitors, tracking snippets, and intent scoring.',
    icon: GlobeIcon,
    hue: 'rose',
  },
  invoices: {
    description: 'Billing, invoice status, and payment workflows.',
    icon: ReceiptIcon,
    hue: 'rose',
  },
  'my-organization': {
    description: 'Your company home: pages, org chart, and shared resources.',
    icon: TreeStructureIcon,
    hue: 'slate',
  },
  'my-work': {
    description: 'Your personal task hub, updates inbox, and reviews.',
    icon: ListChecksIcon,
    hue: 'emerald',
  },
  notes: {
    description: 'Notes, meeting recordings, and knowledge capture.',
    icon: NoteIcon,
    hue: 'amber',
  },
  projects: {
    description: 'Project setup, delivery, budgets, and client work.',
    icon: FolderOpenIcon,
    hue: 'blue',
  },
  settings: {
    description: 'Workspace preferences, columns, and terminology.',
    icon: GearSixIcon,
    hue: 'slate',
  },
  suppliers: {
    description: 'Supplier directory, purchase orders, and performance.',
    icon: TruckIcon,
    hue: 'amber',
  },
  tasks: {
    description: 'Task planning, assignments, and day-to-day execution.',
    icon: CompassIcon,
    hue: 'emerald',
  },
  team: {
    description: 'Members, roles, capacity, staffing, and rates.',
    icon: UsersThreeIcon,
    hue: 'amber',
  },
  tickets: {
    description: 'Support inboxes, ticket stages, and customer email.',
    icon: TicketIcon,
    hue: 'amber',
  },
  time: {
    description: 'Timers, timesheets, approvals, and time reports.',
    icon: TimerIcon,
    hue: 'orange',
  },
  workflows: {
    description: 'Automations with triggers, conditions, and actions.',
    icon: LightningIcon,
    hue: 'orange',
  },
  workstream: {
    description: 'A live stream of work happening across your team.',
    icon: PulseIcon,
    hue: 'sky',
  },
  marketing: {
    description: 'Campaigns, journeys, audience segments, and performance.',
    icon: MegaphoneIcon,
    hue: 'rose',
  },
  scheduling: {
    description: 'Booking links, availability, meetings, and branding.',
    icon: CalendarCheckIcon,
    hue: 'orange',
  },
  'live-chat': {
    description: 'Website conversations, responders, and customer follow-up.',
    icon: ChatsCircleIcon,
    hue: 'emerald',
  },
  quotes: {
    description: 'Pricing proposals, e-signatures, and accepted work.',
    icon: ReceiptIcon,
    hue: 'indigo',
  },
  products: {
    description: 'Your product and service price book, plus on-hand stock.',
    icon: StorefrontIcon,
    hue: 'teal',
  },
  'knowledge-base': {
    description: 'Customer-facing articles, categories, and publishing.',
    icon: BookOpenIcon,
    hue: 'blue',
  },
}

const FALLBACK: CategoryMeta = {
  description: 'Help articles and workflow guidance.',
  icon: FolderOpenIcon,
  hue: 'slate',
}

export function formatLabel(value?: string | null) {
  if (!value) return 'Help'
  const normalized = value.toLowerCase()
  if (LABEL_OVERRIDES[normalized]) return LABEL_OVERRIDES[normalized]
  return value
    .split(/[/\-_]/)
    .filter(Boolean)
    .map(part => {
      const lower = part.toLowerCase()
      return (
        LABEL_OVERRIDES[lower] ?? part.charAt(0).toUpperCase() + part.slice(1)
      )
    })
    .join(' ')
}

export function getCategoryMeta(slug?: string | null): CategoryMeta {
  if (!slug) return FALLBACK
  return CATEGORIES[slug] ?? FALLBACK
}

export function getCategoryLabel(slug?: string | null) {
  const meta = slug ? CATEGORIES[slug] : undefined
  return meta?.label ?? formatLabel(slug)
}

export const ARTICLE_TYPE_LABELS: Record<string, string> = {
  workflow: 'How-to',
  concept: 'Concept',
  reference: 'Reference',
}

export function formatType(type: string) {
  return ARTICLE_TYPE_LABELS[type] ?? formatLabel(type)
}

/** Category display order: pinned "start here" first, then alphabetical. */
export function sortCategories<T extends { slug: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ai = START_HERE_SLUGS.indexOf(a.slug)
    const bi = START_HERE_SLUGS.indexOf(b.slug)
    if (ai !== -1 || bi !== -1) {
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    }
    return getCategoryLabel(a.slug).localeCompare(getCategoryLabel(b.slug))
  })
}
